import React from 'react';
import { useNavigate } from 'react-router-dom';
import Breadcrumb from '@/components/Breadcrumb/Breadcrumb';
import PageNavigation from '@/components/PageNavigation/PageNavigation';
import SubPageHero from '@/components/SubPageHero/SubPageHero';
import StackedContent from '@/components/StackedContent/StackedContent';
import ShippingItem from '@/components/ShippingItem/ShippingItem';
import StatusMarker from '@components/StatusMarker/StatusMarker';
import FAQSection from '@/components/FAQSection/FAQSection';
import heroUnion from '@/assets/icons/material-page-key.svg';
import { useOverviewContext } from '@context/OverviewContext';
import { useSelectedStudent } from '@context/SelectedStudentContext';
import { useTasklist } from '@/context/TasklistContext';
import {
  getTasklistId,
  getTasklistNavigationMeta,
  getLearningMaterialContent,
} from '@/utils/tasklistLegacyNavigation';

const LearningMaterials = () => {
  const { schoolTypeConfigured } = useOverviewContext();
  const { selectedStudent } = useSelectedStudent();

  // checklist
  const { tasklistData } = useTasklist();
  const checklistId = getTasklistId(tasklistData, 'LearningMaterials');
  const isReadyToRender =
    selectedStudent?.accountId !== undefined &&
    selectedStudent?.accountId !== null &&
    typeof checklistId === 'number';

  // Page Navigation Feature
  const navigate = useNavigate();
  const {
    currentIndex,
    totalTasks,
    previousSubtitle,
    nextSubtitle,
    nextSlug,
    prevSlug,
  } = getTasklistNavigationMeta(tasklistData, window.location.pathname, selectedStudent?.schoolId);

  const handleNext = () => {
    if (nextSlug) {
      navigate(`/tasklist/${nextSlug}`);
    }
  };

  const handlePrevious = () => {
    if (prevSlug) {
      navigate(`/tasklist/${prevSlug}`);
    }
  };

  const breadcrumbItems = [
    { title: 'Home', path: '/' },
    { title: 'School prep tasklist', path: '/tasklist' },
    { title: 'Receiving Your Learning Materials' },
  ];

  const { heroDescription, suppliesDescription, faqs, disclaimer } =
    getLearningMaterialContent(schoolTypeConfigured);

  return (
    <div className="learning-material-container">
      <div className="sub-page-hero">
        <Breadcrumb items={breadcrumbItems} />
          <div className="subpage-hero-container">
            <SubPageHero
              theme="primary"
              title="Receiving Your Learning Materials"
              description={heroDescription}
              image={heroUnion}
            />
        </div>
      </div>

      <StackedContent>
        <StackedContent.Col1 cols="10">
          <h2 className='h2'>Tracking Information</h2>
          <ShippingItem />
        </StackedContent.Col1>
      </StackedContent>

      <section className="section-2">
        <StackedContent>
          <StackedContent.Col1 cols="10">
            <h2 className="heading-2">When to Expect K12-Provided Materials </h2>
            <p className="content-block body-m">
              The materials a student needs depend on their courses, so you'll hear from a school staff member soon after enrollment is complete. Depending on grade and course level, your school may need to complete a transcript analysis to place your student in appropriate courses. Once courses are assigned, materials ship automatically and will be delivered within four to seven business days after shipment. 
            </p>
          </StackedContent.Col1>
        </StackedContent>
      </section>

      <section className="section-3">
        <StackedContent>
          <StackedContent.Col1 cols="10">
            <h2 className="heading-2">School Supplies You’ll Need to Provide</h2>
            <p className="content-block body-m">{suppliesDescription}</p>
          </StackedContent.Col1>
        </StackedContent>
      </section>

      <section className="section-learning-material-FAQ">
        <StackedContent>
          <StackedContent.Col1 cols="10">
            <FAQSection title="FAQs" faqs={faqs} />
          </StackedContent.Col1>
        </StackedContent>
      </section>

      <section className="section-5">
        <StackedContent>
          <StackedContent.Col1 cols="10">
            <p className="content-block">{disclaimer}</p>
          </StackedContent.Col1>
        </StackedContent>

        <StackedContent>
          <StackedContent.Col1 cols="10">
            {isReadyToRender && (
              <StatusMarker
                accountId={selectedStudent!.accountId}
                checklistId={checklistId}
              />
            )}
          </StackedContent.Col1>
        </StackedContent>
      </section>

      <section className="navigation">
        <StackedContent>
          <StackedContent.Col1 cols="10">
            <PageNavigation
              currentIndex={currentIndex}
              totalTasks={totalTasks}
              onPrevious={handlePrevious}
              onNext={handleNext}
              previousLabel="Previous Task"
              nextLabel="Next Task"
              previousSubtitle={previousSubtitle}
              nextSubtitle={nextSubtitle}
            />
          </StackedContent.Col1>
        </StackedContent>
      </section>

      <br /><br />
    </div>
  );
};

export default LearningMaterials;
